// Stable species-identity helpers shared by the Worker and the client.

// Normalize a scientific name to a genus+species key (drops author,
// subspecies, parenthetical text, punctuation, casing). This is the
// *stable* identity for a species across data sources and across visits.
//
// `taxon_id` is deliberately NOT used as a stable key: it's a real
// iNaturalist id when iNat returns the species, but a synthetic hash when
// only GBIF/OBIS do. iNaturalist is best-effort (rate limits, transient
// failures), so the same fish at the same spot can come back with a real
// id on one visit and a synthetic id on the next. Anything that needs to
// recognise "this is the same species I saw before" must compare on this
// name key, not on taxon_id alone.
export function normName(s: string): string {
  const cleaned = s
    .replace(/\([^)]*\)/g, " ")
    .toLowerCase()
    .replace(/[^a-z\s]/g, " ")
    .trim()
    .split(/\s+/);
  return cleaned.slice(0, 2).join(" ");
}

// True when `common` is a genuine common name rather than the merge
// fallback. When no data source supplies a vernacular name, mergeSpecies
// sets common_name to the scientific name so the UI always has something
// to show — this distinguishes that case from a real common name.
export function hasRealCommonName(
  common: string,
  scientific: string,
): boolean {
  const c = (common ?? "").trim();
  if (!c) return false;
  return normName(c) !== normName(scientific);
}

// Genus-based detection for the phylum Cnidaria (hard/soft corals, sea
// fans, anemones, hydroids, jellies). iNat's species_counts endpoint often
// returns Cnidaria without ancestor_ids and its iconic-taxon set skips the
// phylum entirely, so the ingest categorizer can't tag them from ancestor
// info alone and they end up as "Other". This name-based check backstops
// the sort (and downstream category assignment) so corals sink even when
// their cached category is still "Other" — no cache flush required.
const CNIDARIA_GENERA: ReadonlySet<string> = new Set([
  // Scleractinia — hard corals
  "acropora", "montipora", "porites", "pocillopora", "millepora", "favia",
  "favites", "platygyra", "goniastrea", "dipsastraea", "goniopora",
  "pavona", "fungia", "leptastrea", "cyphastrea", "diploastrea", "isopora",
  "stylophora", "seriatopora", "turbinaria", "astreopora", "coscinaraea",
  "symphyllia", "lobophyllia", "echinophyllia", "herpolitha", "podabacia",
  "gardineroseris", "psammocora", "coelastrea", "merulina", "sandalolitha",
  "pectinia", "mycedium", "oxypora", "echinopora", "galaxea", "alveopora",
  "physogyra", "plerogyra", "blastomussa", "micromussa", "duncanopsammia",
  "balanophyllia", "tubastraea", "hydnophora", "leptoseris", "pachyseris",
  "acanthastrea", "moseleya", "trachyphyllia", "caulastraea",
  // Alcyonacea + Malacalcyonacea — soft corals, gorgonians, sea fans
  "sarcophyton", "sinularia", "lobophytum", "dendronephthya", "cladiella",
  "xenia", "anthelia", "heteroxenia", "litophyton", "nephthea",
  "alcyonium", "gorgonia", "muricea", "muriceopsis", "junceella",
  "ellisella", "melithaea", "subergorgia", "pterogorgia", "antipathes",
  "eunicea", "plexaura", "plexaurella", "leptogorgia", "pseudopterogorgia",
  // Actiniaria + Zoantharia + Corallimorpharia — anemones, zoanthids,
  // corallimorphs (Palythoa / Zoanthus are the common false-corals)
  "actinia", "anthopleura", "bunodosoma", "bunodactis", "cerianthus",
  "condylactis", "heteractis", "stichodactyla", "entacmaea", "actinodendron",
  "corynactis", "discosoma", "rhodactis", "ricordea", "palythoa", "zoanthus",
  "protopalythoa", "parazoanthus", "epizoanthus", "isaurus",
  // Scyphozoa + Cubozoa — true jellies + box jellies
  "aurelia", "chrysaora", "cyanea", "pelagia", "rhopilema", "cassiopea",
  "mastigias", "chironex", "carybdea", "tripedalia", "linuche", "aequorea",
  "rhizostoma", "phyllorhiza", "chiropsalmus", "malo",
  // Siphonophora + Hydrozoa surface
  "physalia", "velella", "porpita",
]);

export function isCnidarianName(scientific: string): boolean {
  const key = normName(scientific);
  if (!key) return false;
  const genus = key.split(" ", 1)[0];
  return CNIDARIA_GENERA.has(genus);
}
